import { QueryClient } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { ApiError } from '@shared/api/client';
import { GC_TIME, STALE_TIME } from '@shared/api/query-keys';
import { warmupServices } from '@shared/api/warmup';
import { installGlobalErrorHandlers } from '@shared/telemetry/errors';
import { installWebVitals } from '@shared/telemetry/web-vitals';

import { App } from './App';
import './styles/global.css';

/** Retried statuses: a transient upstream, never a refused or invalid request. */
const RETRYABLE_STATUSES = new Set([502, 504]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME.list,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        if (error instanceof ApiError) return RETRYABLE_STATUSES.has(error.status);
        // A network error before any response — worth one more attempt.
        return true;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000) + Math.random() * 250,
    },
    mutations: {
      // **Mutations never auto-retry.** A hard removal whose outcome is unknown
      // is not replayed from the client — the surface says the outcome is
      // unknown and directs to a reload.
      retry: false,
    },
  },
});

window.__BUILD_SHA__ = import.meta.env.VITE_BUILD_SHA;

installGlobalErrorHandlers();
installWebVitals();

async function start(): Promise<void> {
  // The mock worker is imported behind this branch and awaited before the
  // mount, so no real request escapes during startup and neither MSW nor any
  // fixture enters a production bundle (`frontend-layout.md` §10.10).
  if (import.meta.env.DEV) {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html.');

  createRoot(container).render(
    <StrictMode>
      <App queryClient={queryClient} />
    </StrictMode>,
  );

  // Unauthenticated by contract, idle-scheduled, and silently swallowed: a
  // warmup never blocks a render and never affects circuit-breaker state.
  warmupServices();
}

void start();
